// URL <-> filter state for the Payment Failures report (PROD-9152); pure, so it is testable.

import moment from 'moment-timezone';
import { date_drop_for_report } from '../../../../constants';
import { get_date_filter_data } from '../../../../util_functions';

// The report opens on today.
export const DEFAULT_DATE_FILTER = 'TODAY';
export const PAGE_SIZE = 50;

const VALID_DATE_FILTERS = date_drop_for_report.map((option) => option.value);

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// An unrecognised value in a hand-edited URL falls back to the default instead of reaching the server.
const one_of = (value, allowed, fallback) => (allowed.indexOf(value) === -1 ? fallback : value);

const a_date = (value, fallback) => (DATE_PATTERN.test(value || '') ? value : fallback);

const a_page = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.floor(parsed) < 1 ? 1 : Math.floor(parsed);
};

// `fallback_date` is today, passed in by the caller so this module needs no clock of its own.
export const parse_filters = (search, fallback_date) => {
  const params = new URLSearchParams(search || '');

  return {
    date_filter_type: one_of(params.get('date_filter'), VALID_DATE_FILTERS, DEFAULT_DATE_FILTER),
    start_date: a_date(params.get('start_date'), fallback_date),
    end_date: a_date(params.get('end_date'), fallback_date),
    page: a_page(params.get('page'))
  };
};

// Only what differs from the defaults, so the default first page has no query string.
export const build_search = (filters) => {
  const params = new URLSearchParams();

  if (filters.date_filter_type && filters.date_filter_type !== DEFAULT_DATE_FILTER) {
    params.set('date_filter', filters.date_filter_type);
  }
  if (filters.date_filter_type === 'CUSTOM') {
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
  }
  if (filters.page > 1) params.set('page', String(filters.page));

  const query = params.toString();
  return query === '' ? '' : `?${query}`;
};

// The server pages by offset; the URL carries a 1-based page so a shared link reads naturally.
export const offset_for_page = (page) => (a_page(page) - 1) * PAGE_SIZE;

const UTC_FORMAT = 'YYYY-MM-DD HH:mm:ss';
// Both shapes get_date_filter_data emits. Passing them explicitly matters: without a format list
// moment falls back to Date.parse, which ignores the zone and returns the local string unconverted.
const LOCAL_FORMATS = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'];

// get_date_filter_data emits three different end shapes. Naming them beats inferring from the value:
// LAST MONTH also ends at midnight but is the START of the last day, so treating midnight as
// "already exclusive" silently dropped that whole day.
const EXCLUSIVE_END = ['YESTERDAY', 'LAST WEEK', 'CUSTOM'];
const INCLUSIVE_DAY_END = ['LAST MONTH'];

const is_midnight = (m) => m.hours() === 0 && m.minutes() === 0 && m.seconds() === 0;

// Normalise every preset to one exclusive UTC instant the server compares with `<`.
export const utc_window = (date_filter_type, start_date, end_date, iana_timezone) => {
  const zone = iana_timezone || 'America/Los_Angeles';
  const local = get_date_filter_data(date_filter_type || DEFAULT_DATE_FILTER, start_date, end_date);

  const start = moment.tz(local.final_start_date, LOCAL_FORMATS, zone);
  const end = moment.tz(local.final_end_date, LOCAL_FORMATS, zone);

  // A hand-edited URL can reach here with an unparseable date; fall back to today rather than
  // sending the server a malformed window.
  if (!start.isValid() || !end.isValid()) {
    const day = moment.tz(zone).startOf('day');
    return {
      start: day.clone().utc().format(UTC_FORMAT),
      end: day.clone().add(1, 'day').utc().format(UTC_FORMAT)
    };
  }

  const type = date_filter_type || DEFAULT_DATE_FILTER;
  let exclusive_end;
  if (EXCLUSIVE_END.indexOf(type) !== -1) {
    exclusive_end = end;
  } else if (INCLUSIVE_DAY_END.indexOf(type) !== -1) {
    exclusive_end = end.clone().add(1, 'day');
  } else {
    // Inclusive instant (23:59:59 or a live HH:mm). The midnight check is the fallback for a preset
    // added later without updating the lists above.
    exclusive_end = is_midnight(end) ? end : end.clone().add(1, 'second');
  }
  return {
    start: start.utc().format(UTC_FORMAT),
    end: exclusive_end.utc().format(UTC_FORMAT)
  };
};
